// src/features/context/ManifestsPanel.tsx
// Registry of aspect and relation-group manifests — normally registered by
// profile services at startup; this panel lets an admin browse them and,
// for third-party/manual cases, register one by pasting its JSON body.
// See docs/ux/context-ia.md section 2.

import { useEffect, useState } from 'react';
import { useI18n, useContext as useContextStore } from '@react/hooks';
import { Button, Textarea, ErrorAlert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { ListManifests } from '@core/api/endpoints/context/list_manifests';
import type { AspectTypeManifest, RelationGroupManifest } from '@core/types/context';
import { tryParseJson } from './logic';

function RegisterAspectForm({ onRegistered }: { onRegistered: () => void }) {
  const { t } = useI18n();
  const store = useContextStore();
  const [text, setText] = useState('{\n  "aspect_type": "",\n  "owner_product_id": "",\n  "display_name": "",\n  "category": ""\n}');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { value, error: parseError } = tryParseJson<AspectTypeManifest>(text);
    if (parseError || !value?.aspect_type) {
      setError(parseError ?? t('context.manifests.aspectTypeRequired'));
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      await store.RegisterAspectManifest(value.aspect_type, { manifest: value });
      onRegistered();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Textarea label={t('context.manifests.aspectJson')} value={text} onChange={(e) => setText(e.target.value)} rows={8} className="font-mono text-xs" />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" size="sm" disabled={isSaving}>{isSaving ? t('common.saving') : t('context.manifests.registerAspect')}</Button>
    </form>
  );
}

function RegisterRelationGroupForm({ onRegistered }: { onRegistered: () => void }) {
  const { t } = useI18n();
  const store = useContextStore();
  const [text, setText] = useState('{\n  "group_key": "",\n  "owner_product_id": "",\n  "display_name": "",\n  "relation_types": []\n}');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { value, error: parseError } = tryParseJson<RelationGroupManifest>(text);
    if (parseError || !value?.group_key) {
      setError(parseError ?? t('context.manifests.groupKeyRequired'));
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      await store.RegisterRelationGroupManifest(value.group_key, { manifest: value });
      onRegistered();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Textarea label={t('context.manifests.relationJson')} value={text} onChange={(e) => setText(e.target.value)} rows={8} className="font-mono text-xs" />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" size="sm" disabled={isSaving}>{isSaving ? t('common.saving') : t('context.manifests.registerRelationGroup')}</Button>
    </form>
  );
}

export function ManifestsPanel() {
  const { t } = useI18n();
  const store = useContextStore();
  const [aspects, setAspects] = useState<AspectTypeManifest[]>([]);
  const [relationGroups, setRelationGroups] = useState<RelationGroupManifest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [showRegister, setShowRegister] = useState<'aspect' | 'relation' | null>(null);

  const load = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      // The generated store method only surfaces aspect_manifests and drops
      // relation_manifests — call the endpoint directly to get the full response.
      const r = await ListManifests({ sort: [], view: 0, pagination: { limit: 100, cursor: '', direction: 'forward' } });
      setAspects(r.aspect_manifests?.manifests ?? []);
      setRelationGroups(r.relation_manifests?.manifests ?? []);
    } catch (err) {
      setLoadError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {loadError != null && <ErrorAlert error={loadError} dismissible onDismiss={() => setLoadError(null)} />}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">{t('context.manifests.aspectsTitle')}</h3>
          <Button variant="ghost" size="sm" onClick={() => setShowRegister(showRegister === 'aspect' ? null : 'aspect')}>
            {t('context.manifests.registerAspect')}
          </Button>
        </div>
        {showRegister === 'aspect' && <RegisterAspectForm onRegistered={() => { setShowRegister(null); load(); }} />}
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('context.manifests.col.aspectType')}</TableHead>
              <TableHead>{t('context.manifests.col.owner')}</TableHead>
              <TableHead>{t('context.manifests.col.category')}</TableHead>
              <TableHead>{t('context.manifests.col.priority')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-6 text-sm text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : aspects.length === 0 ? (
              <TableEmpty colSpan={4} message={t('context.manifests.noAspects')} />
            ) : (
              aspects.map((a) => (
                <TableRow key={a.aspect_type}>
                  <TableCell className="font-mono text-xs">{a.aspect_type}</TableCell>
                  <TableCell>{a.owner_product_id}</TableCell>
                  <TableCell>{a.category}</TableCell>
                  <TableCell>{a.priority}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">{t('context.manifests.relationGroupsTitle')}</h3>
          <Button variant="ghost" size="sm" onClick={() => setShowRegister(showRegister === 'relation' ? null : 'relation')}>
            {t('context.manifests.registerRelationGroup')}
          </Button>
        </div>
        {showRegister === 'relation' && <RegisterRelationGroupForm onRegistered={() => { setShowRegister(null); load(); }} />}
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('context.manifests.col.groupKey')}</TableHead>
              <TableHead>{t('context.manifests.col.owner')}</TableHead>
              <TableHead>{t('context.manifests.col.category')}</TableHead>
              <TableHead>{t('context.manifests.col.relationTypes')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-6 text-sm text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : relationGroups.length === 0 ? (
              <TableEmpty colSpan={4} message={t('context.manifests.noRelationGroups')} />
            ) : (
              relationGroups.map((g) => (
                <TableRow key={g.group_key}>
                  <TableCell className="font-mono text-xs">{g.group_key}</TableCell>
                  <TableCell>{g.owner_product_id}</TableCell>
                  <TableCell>{g.category}</TableCell>
                  <TableCell>{g.relation_types.length}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
